import React, { useContext, useState } from 'react';
import { css } from '@emotion/react';
import { Grid, LinearProgress, MenuItem, TextField } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { useMutation } from '@apollo/client';

import { UserContext } from '@/components/App';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { INSERT_CORP_INTEGRATION_OVERRIDE, GET_CORP_INTEGRATION_OVERRIDES } from '../graphql';
import { CorpAdminContext } from '../store';

const ConnectShipperModal = ({ open, onClose, carrier_id, connectedShipperIds }) => {
    const { admin_clients } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;

    const [shipperId, setShipperId] = useState('');

    const orgShipperIds = (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id);

    const [insertOverride, { loading }] = useMutation(INSERT_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data }) {
            const newOverride = data?.insert_integration_overrides_one;
            if (!newOverride) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                    data: {
                        integration_overrides: [...existing.integration_overrides, newOverride],
                    },
                });
            } catch (err) {
                // Cache miss is non-fatal — store will pick it up on next read.
            }
        },
    });

    const availableShippers = (admin_clients || []).filter(
        (c) => !!c.user?.roles?.['SHIPPER'] && !connectedShipperIds.has(c.client_id)
    );

    const handleClose = () => {
        setShipperId('');
        onClose();
    };

    const handleSubmit = async () => {
        // Marketplace connections have no carrier; the shipper itself owns the override row.
        const isMarketplace = !carrier_id;
        try {
            await insertOverride({
                variables: {
                    object: {
                        carrier_id: isMarketplace ? null : carrier_id,
                        shipper_id: shipperId,
                        client_id: isMarketplace ? shipperId : carrier_id,
                        type: 'MANUAL',
                    },
                },
            });
            setNotification?.({ severity: 'success', message: 'Shipper connected' });
            handleClose();
        } catch (err) {
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Error connecting shipper' });
        }
    };

    return (
        <NavResponsiveModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <ModalHeader
                title="Connect Shipper"
                onClose={handleClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            {loading && (
                <LinearProgress
                    color="primary"
                    css={css`
                        width: 100%;
                    `}
                />
            )}
            <ModalContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            variant="outlined"
                            fullWidth
                            label="Shipper"
                            value={shipperId}
                            onChange={(e) => setShipperId(e.target.value)}
                        >
                            {availableShippers.length === 0 ? (
                                <MenuItem disabled value="">
                                    No shippers available
                                </MenuItem>
                            ) : (
                                availableShippers.map((s) => (
                                    <MenuItem key={s.client_id} value={s.client_id}>
                                        {s.business_name || s.email || s.client_id}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>
                </Grid>
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={!shipperId || loading} onClick={handleSubmit}>
                    Connect
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default ConnectShipperModal;
